import * as chrono from "chrono-node";

/**
 * Normalizes any timestamp input to Unix timestamp in milliseconds (epoch ms).
 * Handles:
 * - Seconds (e.g. 1785758054 or 1785758054.365 from Python time.time())
 * - Milliseconds (e.g. 1785758054365)
 * - Microseconds (e.g. 1785758054365080 from microsecond timestamps)
 * - Nanoseconds (e.g. 1785758054365080000 from Go/Python time_ns())
 * - Invalid/missing timestamps (returns 0)
 */
export const normalizeTimestamp = (t: number | undefined | null): number => {
  if (
    t === undefined || t === null || typeof t !== "number" || isNaN(t) || t <= 0
  ) {
    return 0;
  }
  // Unix timestamp in seconds (between 1973 and 5138, e.g., ~1.7e9)
  if (t < 1e11) {
    return Math.round(t * 1000);
  }
  // Unix timestamp in microseconds (~1.7e15)
  if (t > 1e14 && t <= 1e17) {
    return Math.round(t / 1000);
  }
  // Unix timestamp in nanoseconds (~1.7e18)
  if (t > 1e17) {
    return Math.round(t / 1000000);
  }
  // Already in milliseconds (~1.7e12)
  return Math.round(t);
};

/**
 * Formats elapsed millisecond duration into a clean human-readable relative string.
 */
export const formatElapsed = (deltaMs: number): string => {
  if (!deltaMs || isNaN(deltaMs) || deltaMs <= 0) return "+0 ms";
  const safeDelta = Math.max(0, deltaMs);
  if (safeDelta < 1000) return `+${Math.round(safeDelta)} ms`;
  const sec = Math.floor(safeDelta / 1000);
  if (sec < 60) return `+${(safeDelta / 1000).toFixed(1)}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) {
    const s = sec % 60;
    return s > 0 ? `+${min}m ${s}s` : `+${min}m`;
  }
  const hrs = Math.floor(min / 60);
  if (hrs < 24) {
    const m = min % 60;
    return m > 0 ? `+${hrs}h ${m}m` : `+${hrs}h`;
  }
  const d = Math.floor(hrs / 24);
  const h = hrs % 24;
  return h > 0 ? `+${d}d ${h}h` : `+${d}d`;
};

/**
 * Normalizes user input for relative time ranges, correcting common typos and shorthands.
 */
export const normalizeSinceInput = (s: string): string =>
  s.replace(/\bhous\b/gi, "hours")
    .replace(/\bhr\b/gi, "hour")
    .replace(/\bhrs\b/gi, "hours")
    .replace(/\bmin\b/gi, "minute")
    .replace(/\bmins\b/gi, "minutes")
    .replace(/\bsec\b/gi, "second")
    .replace(/\bsecs\b/gi, "seconds")
    .replace(/\bwk\b/gi, "week")
    .replace(/\bwks\b/gi, "weeks")
    .replace(/\bmo\b/gi, "month")
    .replace(/\bmos\b/gi, "months")
    .replace(/\byr\b/gi, "year")
    .replace(/\byrs\b/gi, "years");

/**
 * Parses user input date string (e.g. "last 24 hours", "yesterday", "last 24 hous")
 * into a Date object, or null if input is empty or unparseable.
 */
export const parseSinceDate = (
  raw: string | null | undefined,
  referenceDate = new Date(),
): Date | null => {
  const v = raw?.trim() ?? "";
  if (!v) return null;
  const normalized = normalizeSinceInput(v);
  try {
    return chrono.parse(normalized, referenceDate)?.[0]?.date() ?? null;
  } catch {
    return null;
  }
};

